import { useCallback, useEffect, useState } from 'react'
import { getCookie } from 'src/common/helpers'
import { getVisibleConsentBannerHeight } from 'src/common/consentBanner'
import {
  PROSPECT_QUIZ_NUDGE_DELAY_MS,
  PROSPECT_QUIZ_NUDGE_HANDLED_KEY,
  PROSPECT_QUIZ_NUDGE_SESSION_START_KEY,
} from 'src/common/constants/prospectQuizNudge'

const LEAD_SUBMITTED_KEY = 'leadSubmitted'
const CONSENT_POLL_MS = 1000
const DESKTOP_QUERY = '(min-width: 1024px)'

const isBrowser = () => typeof window !== 'undefined'

const isDesktopViewport = () =>
  isBrowser() && Boolean(window.matchMedia?.(DESKTOP_QUERY)?.matches)

/** Session start in epoch ms; written once per session, on any page. */
const ensureSessionStart = () => {
  try {
    const stored = Number(
      sessionStorage.getItem(PROSPECT_QUIZ_NUDGE_SESSION_START_KEY)
    )
    if (stored > 0) return stored

    const now = Date.now()
    sessionStorage.setItem(PROSPECT_QUIZ_NUDGE_SESSION_START_KEY, String(now))

    return now
  } catch {
    return null
  }
}

const canShowNudge = () => {
  try {
    if (sessionStorage.getItem(PROSPECT_QUIZ_NUDGE_HANDLED_KEY) === 'true')
      return false
    if (sessionStorage.getItem(LEAD_SUBMITTED_KEY) === 'true') return false
  } catch {
    return false
  }

  // Exclude clients headed to /login.
  return getCookie('showForm') !== 'true'
}

/**
 * Shows the prospect quiz nudge after 45s of session time (DCON-3903).
 *
 * - The 45s count across page views: the session start is kept in
 *   sessionStorage and every page waits only for what is left.
 * - Once engaged with or dismissed, it stays hidden for the session.
 * - On mobile it waits for the consent banner to be dismissed; on desktop it
 *   stacks above the banner (`consentBannerOffset`).
 * - `isBlocked` holds it back while another popup is open.
 */
export function useProspectQuizNudge({ enabled, isBlocked = false }) {
  const [timeReached, setTimeReached] = useState(false)
  const [handled, setHandled] = useState(false)
  const [consent, setConsent] = useState({ checked: false, height: 0 })

  // Record the session start on every page so time outside non-brand pages
  // counts too, independently of the flag request.
  useEffect(() => {
    ensureSessionStart()
  }, [])

  useEffect(() => {
    if (!enabled || !isBrowser()) return undefined

    const start = ensureSessionStart()
    if (start === null) return undefined

    const remaining = Math.max(
      0,
      PROSPECT_QUIZ_NUDGE_DELAY_MS - (Date.now() - start)
    )
    const timer = setTimeout(() => {
      if (canShowNudge()) setTimeReached(true)
    }, remaining)

    return () => clearTimeout(timer)
  }, [enabled])

  const isDesktop = isDesktopViewport()
  const waitingOrShown = enabled && timeReached && !handled

  useEffect(() => {
    if (!waitingOrShown) return undefined

    const check = () => {
      const height = getVisibleConsentBannerHeight()
      setConsent((prev) =>
        prev.checked && prev.height === height
          ? prev
          : { checked: true, height }
      )

      return height
    }

    if (check() === 0) return undefined

    const interval = setInterval(() => {
      if (check() === 0) clearInterval(interval)
    }, CONSENT_POLL_MS)

    return () => clearInterval(interval)
  }, [waitingOrShown])

  const isCoveredByConsentBanner = consent.height > 0 && !isDesktop
  const showNudge =
    waitingOrShown && consent.checked && !isBlocked && !isCoveredByConsentBanner

  const markHandled = useCallback(() => {
    try {
      sessionStorage.setItem(PROSPECT_QUIZ_NUDGE_HANDLED_KEY, 'true')
    } catch {
      // ignore storage failures; the in-memory flag still hides it
    }

    setHandled(true)
  }, [])

  return {
    showNudge,
    consentBannerOffset: isDesktop ? consent.height : 0,
    handleEngage: markHandled,
    handleDismiss: markHandled,
  }
}
